import React, { useEffect, useState, useRef } from 'react'
import { useUpdateUserEmailMutation } from '../../../services/authApi';
import { useFormik } from 'formik';
import * as Yup from 'yup'
import { toast } from 'react-toastify';
import TextField from '../../../components/textFields/TextField';
import { KTSVG } from '../../../_metronic/helpers';
import { Modal, Button, Tooltip, Overlay, Card, Badge } from 'react-bootstrap';
import labelKey from '../../localization/label.json'
import moment from 'moment';
import { useForgotPasswordMutation } from '../../../services/authenticationApi'

type Props = {
    show: boolean
    handleClose: () => void
    data?: any
    refetch?: any
}

const PasswordResetModal = ({ show, handleClose, data, refetch }: Props) => {
    const [id, setId] = useState(0);
    const [copied, setCopied] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [timeLeft, setTimeLeft] = useState<string>('');
    const [isExpired, setIsExpired] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Call the hook at the top level, not inside a function
    const [forgotPasswordTrigger, { data: resetPasswordData, isLoading, isSuccess, isError, error }] = useForgotPasswordMutation()

    // Calculate expiry time (72 hours from request date)
    useEffect(() => {
        if (data?.passwordResetRequestDate) {
            const updateTimer = () => {
                const requestDate = moment(data.passwordResetRequestDate);
                const expiryDate = requestDate.add(72, 'hours');
                const now = moment();

                if (now.isAfter(expiryDate)) {
                    setIsExpired(true);
                    setTimeLeft('Link expired');
                    return;
                }

                const duration = moment.duration(expiryDate.diff(now));
                const days = Math.floor(duration.asDays());
                const hours = duration.hours();
                const minutes = duration.minutes();
                const seconds = duration.seconds();

                if (days > 0) {
                    setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
                } else if (hours > 0) {
                    setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
                } else if (minutes > 0) {
                    setTimeLeft(`${minutes}m ${seconds}s`);
                } else {
                    setTimeLeft(`${seconds}s`);
                }
            };

            // Update immediately
            updateTimer();

            // Update every second
            const intervalId = setInterval(updateTimer, 1000);

            return () => clearInterval(intervalId);
        }
    }, [data?.passwordResetRequestDate]);

    const handleModalClose = () => {
        setCopied(false);
        setShowTooltip(false);
        handleClose();
    };

    const handleCopyToClipboard = () => {
        if (data?.passwordResetLink) {
            navigator.clipboard.writeText(data.passwordResetLink)
                .then(() => {
                    setCopied(true);
                    toast.success('Password reset link copied to clipboard!');

                    // Reset copied state after 2 seconds
                    setTimeout(() => {
                        setCopied(false);
                    }, 2000);
                })
                .catch((err) => {
                    console.error('Failed to copy:', err);
                    toast.error('Failed to copy link to clipboard');
                });
        }
    };

    const handleGenerateNewLink = () => {
        if (data?.email) {
            forgotPasswordTrigger({ email: data.email })
                .unwrap()
                .then(() => {
                    if (refetch) {
                        refetch();
                    }
                    handleModalClose(); // Close modal after generating new link
                })
                .catch((err) => {
                });
        }
    };

    useEffect(() => {
        if (isSuccess) {
            toast.success('Reset password link generated!');
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && error) {
            let errorMessage: string = 'Something went wrong';
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [isError, error]);

    return (
        <>
            <Modal
                aria-hidden='true'
                dialogClassName='modal-dialog modal-md modal-dialog-centered'
                show={show}
                onHide={handleModalClose}
            >
                <div className='modal-header'>
                    <h2 className='mb-0'>Reset Password</h2>
                    <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleModalClose}>
                        <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                    </div>
                </div>

                <div className='modal-body py-lg-10 px-lg-10'>
                    {data?.passwordResetLink ? (
                        <div className="text-center">
                            {/* Expiry Timer */}
                            <Card className="mb-6">
                                <Card.Body className="py-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <span className="fw-semibold text-gray-700">Link Expires In:</span>
                                        <Badge
                                            bg={isExpired ? "danger" : "primary"}
                                            className="fs-6 px-3 py-2"
                                        >
                                            {timeLeft}
                                        </Badge>
                                    </div>
                                    <div className="progress h-5px">
                                        <div
                                            className={`progress-bar ${isExpired ? 'bg-danger' : 'bg-primary'}`}
                                            role="progressbar"
                                            style={{ width: isExpired ? '100%' : '75%' }}
                                        ></div>
                                    </div>
                                    <small className="text-muted mt-2 d-block">
                                        {isExpired ? 'This link has expired' : 'Link will expire in 72 hours'}
                                    </small>
                                </Card.Body>
                            </Card>
                            {/* Link Display */}
                            <div className="mb-6">
                                <label className="form-label fw-bold mb-3">Password Reset Link</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control form-control-solid"
                                        value={data.passwordResetLink}
                                        readOnly
                                        placeholder="Password reset link"
                                    />
                                    <button
                                        className={`btn ${copied ? 'btn-success' : 'btn-light-primary'}`}
                                        type="button"
                                        onClick={handleCopyToClipboard}
                                        disabled={isExpired}
                                    >
                                        {copied ? (
                                            <>
                                                <KTSVG
                                                    className="svg-icon-2 me-2"
                                                    path="/media/icons/duotune/general/gen044.svg"
                                                />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <KTSVG
                                                    className="svg-icon-2 me-2"
                                                    path="/media/icons/duotune/files/fil005.svg"
                                                />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="form-text text-muted mt-2">
                                    Click the copy button to copy this link to clipboard
                                </div>
                            </div>

                            {/* Generate New Link Button - Always Show */}
                            <div className="mt-6">
                                <Button
                                    variant="primary"
                                    onClick={handleGenerateNewLink}
                                    disabled={isLoading}
                                    className="d-flex align-items-center justify-content-center gap-2 px-6 py-3 fw-bold"
                                    style={{ minWidth: '250px', margin: '0 auto' }}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <KTSVG
                                                className="svg-icon-3 me-2"
                                                path="/media/icons/duotune/arrows/arr064.svg"
                                            />
                                            Generate New Link
                                        </>
                                    )}
                                </Button>
                                <div className="form-text text-muted mt-2">
                                    Generate a new password reset link (valid for 72 hours)
                                </div>
                            </div>

                            {/* Warning for expired link */}
                            {isExpired && (
                                <div className="alert alert-danger mt-6">
                                    <div className="d-flex align-items-center">
                                        <div>
                                            <div className="fw-bold">Link Expired</div>
                                            <div className="fs-7">
                                                This password reset link has expired. Please generate a new one.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <div className="symbol symbol-100px symbol-circle bg-light-danger mb-6">
                                <div className="symbol-label">
                                    <KTSVG
                                        className="svg-icon-2x svg-icon-danger"
                                        path="/media/icons/duotune/electronics/elc001.svg"
                                    />
                                </div>
                            </div>
                            <h4 className="fw-bold text-gray-700 mb-3">No Password Reset Link</h4>
                            <p className="text-muted mb-6">
                                No password reset link is available for this user.
                            </p>
                            <div className="d-flex justify-content-center gap-3">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleGenerateNewLink}
                                    disabled={isLoading}
                                    className="d-flex align-items-center justify-content-center gap-2 px-6 py-3 fw-bold"
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <KTSVG
                                                className="svg-icon-3 me-2"
                                                path="/media/icons/duotune/arrows/arr064.svg"
                                            />
                                            Generate New Link
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="light"
                                    className="fw-bold px-6"
                                    onClick={handleModalClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    )
}

export default PasswordResetModal